<template>
  <!-- Existing dialogs remain unchanged -->
  <SmDrawer v-model:visibility="viewDialogVisible" class="group-details-drawer">
    <template #title>
      <h2 v-if="editGroup">{{ editGroup.name }}</h2>
    </template>
    <GroupDisplayDescription v-if="editGroup" :group="editGroup" class="mb-4" />
    <GroupDetailsContent
      :group="editGroup!"
      :all-applications="allApplications"
      :all-accounts="allAccounts"
      :all-tags="allTags"
      :all-software-licenses="allSoftwareLicenses"
      :all-contracts="allContracts"
      @group-changed="loadGroups" />
  </SmDrawer>

  <GroupAddDialog
    v-model:visibility="editGroupDialogVisible"
    v-model:account-group="accountGroup"
    :all-contracts
    :all-software-licenses
    :mode="mode"
    @groups-changed="loadGroups" />

  <GroupAddUserDialog
    ref="groupAddUserDialog"
    v-model:visibility="addUserDialogVisible"
    v-model:account-group="editGroup"
    :all-accounts="allAccounts"
    @groups-changed="loadGroups" />

  <DeleteConfirmDialog
    v-model:visible="deleteDialogVisible"
    :title="i18n.t('deleteGroup')"
    :sub-title="i18n.t('deleteGroupText', { name: groupToDelete?.name })"
    @delete="confirmDelete"
    @cancel="deleteDialogVisible = false" />

  <!-- Main Content -->
  <main class="flex h-full flex-col gap-6">
    <!-- Toolbar: Visible if there are groups OR if loading (to prevent layout jump) -->
    <div class="toolbar" v-if="groups.length > 0 || loadingGroups">
      <!-- Left: Title + Search -->
      <div class="toolbar__left">
        <SmInput
          v-model="searchTerm"
          outline
          size="medium"
          :label="i18n.t('search')"
          class="toolbar__search">
          <template #prefix>
            <Search :size="16" class="text-contrast-muted" />
          </template>
        </SmInput>
      </div>

      <!-- Right: Create button only (View toggle removed) -->
      <div class="toolbar__right">
        <SmButton type="primary" size="medium" @click="handleAdd()">
          <Plus :size="16" class="mr-1" />
          {{ i18n.t('addGroup') }}
        </SmButton>
      </div>
    </div>

    <!-- Grid Content (Always Grid) -->
    <div
      v-if="!loadingGroups && groups.length > 0"
      v-auto-animate
      class="group-grid">
      <GroupCard
        v-for="group in filteredGroups"
        :key="group._id"
        :group="group"
        @view="handleView"
        @add-user="handleAddUser(group)"
        @edit="handleEdit(group)"
        @delete="handleDelete(group)" />

      <!-- Compact add card for grid view -->
      <div v-if="!searchTerm" class="group-card-add" @click="handleAdd()">
        <div class="group-card-add__content">
          <div class="group-card-add__icon">
            <Plus :size="24" />
          </div>
          <h4>{{ i18n.t('addGroup') }}</h4>
        </div>
      </div>

      <!-- Search Empty State -->
      <div
        v-if="filteredGroups.length === 0 && searchTerm"
        class="col-span-full py-12 text-center text-contrast-muted">
        <p>{{ i18n.t('noResultsFoundFor', { term: searchTerm }) }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <template v-else-if="loadingGroups">
      <div class="group-grid mt-11">
        <SmSkeleton
          v-for="index in 4"
          :key="index"
          height="160px"
          width="100%"
          class="!rounded-lg" />
      </div>
    </template>

    <!-- Empty State (No groups at all) -->
    <EmptyState
      v-if="!loadingGroups && groups.length === 0"
      :icon="Users"
      :title="capitalize(i18n.t('groupsHeadline'))"
      :text="i18n.t('groupsDescription')"
      :create-label="i18n.t('addGroup')"
      :show-docs-btn="false"
      class="empty-state"
      @create="handleAdd()" />
  </main>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { ref, computed, onMounted } from 'vue'

  import {
    saasmetrix__models__account__AccountGroup__AccountGroup as AccountGroup,
    Account,
    PriceInformation,
    SoftwareLicense,
    Tag,
    TagsService,
    UserGroupsService,
    SoftwareLicensesService,
    AccountGroupIn,
    SoftwareOut,
  } from '@/client'
  import { capitalize } from '@/utils/formatter/formatter'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { PersonStore } from '@/stores/person/person.store'
  import { Plus, Users, Search } from 'lucide-vue-next'

  const i18n = useI18n()

  const { data: allApplications } =
    ApplicationStore.useApplications<SoftwareOut>()
  const allSoftwareLicenses = ref<SoftwareLicense[]>([])
  const allContracts = ref<PriceInformation[]>([])
  const allAccounts = ref<Account[]>([])
  const allTags = ref<Tag[]>([])
  const groups = ref<AccountGroup[]>([])

  // Dialog states
  const viewDialogVisible = ref(false)
  const editGroupDialogVisible = ref(false)
  const addUserDialogVisible = ref(false)
  const deleteDialogVisible = ref(false)
  const groupToDelete = ref<AccountGroup>()

  const accountGroup = computed(() =>
    mode.value === 'add' ? addGroup.value : editGroup.value
  )
  const editGroup = ref<AccountGroup>()
  const addGroup = ref<AccountGroupIn>()
  const mode = ref<'add' | 'edit'>('add')
  const loadingGroups = ref(false)

  // Event handlers
  function handleAddUser(group: AccountGroup) {
    editGroup.value = group
    addUserDialogVisible.value = true
  }

  function handleEdit(group: AccountGroup) {
    editGroup.value = group
    editGroupDialogVisible.value = true
    mode.value = 'edit'
  }

  function handleAdd() {
    const group = ref<AccountGroupIn>({
      name: '',
      accounts: [],
      permission: {
        software_permissions: {},
      },
      color: {
        r: 0,
        g: 0,
        b: 255,
      },
    })
    addGroup.value = group.value
    editGroupDialogVisible.value = true
    mode.value = 'add'
  }

  function handleView(group: AccountGroup) {
    editGroup.value = group
    viewDialogVisible.value = true
  }

  function handleDelete(group: AccountGroup) {
    groupToDelete.value = group
    deleteDialogVisible.value = true
  }

  function confirmDelete() {
    if (!groupToDelete.value?._id) return

    UserGroupsService.deleteAccountGroupApiV1ManagementGroupsAccountGroupIdDelete(
      {
        accountGroupId: groupToDelete.value._id,
      }
    )
      .then(() => {
        deleteDialogVisible.value = false
      })
      .catch((err) => {
        console.error('Failed to delete group:', err)
      })
      .finally(() => {
        loadGroups()
      })
  }

  const searchTerm = ref('')
  const filteredGroups = computed(() => {
    if (!searchTerm.value) return groups.value
    return groups.value.filter((group) => {
      const nameMatch = group.name
        .toLowerCase()
        .includes(searchTerm.value.toLowerCase())
      return nameMatch
    })
  })

  function loadGroups() {
    loadingGroups.value = true
    UserGroupsService.getAccountGroupsApiV1ManagementGroupsGet({})
      .then((res) => {
        groups.value = res.sort((a, b) => a.name.localeCompare(b.name))
        if (editGroup.value) {
          editGroup.value = res.find((g) => g._id == editGroup.value?._id)
        }
      })
      .catch((err) => {
        console.error('Failed to load groups:', err)
      })
      .finally(() => {
        loadingGroups.value = false
      })
  }

  onMounted(async () => {
    try {
      const [tags, licenses, contracts, users] = await Promise.all([
        TagsService.getTagsApiV1ManagementTagsGet({}),
        SoftwareLicensesService.getSoftwareLicensesApiV1SoftwareSoftwareLicensesGet(
          {}
        ),
        SoftwareLicensesService.getSoftwareLicensePriceInformationsApiV1SoftwareSoftwareLicensePriceInformationsGet(
          {}
        ),
        PersonStore.getPersons<Account>(undefined, undefined, undefined, false),
      ])

      allTags.value = tags
      allSoftwareLicenses.value = licenses
      allContracts.value = contracts
      if (users) allAccounts.value = users

      // Load applications
      // ApplicationStore.useApplications is already called at top level

      // Load groups last
      loadGroups()
    } catch (err) {
      console.error('Failed to load initial data:', err)
    }
  })
</script>

<style scoped lang="scss">
  // Improved toolbar styling
  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;
    padding-top: 1rem;
  }

  .toolbar__left {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;
  }

  .toolbar__right {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-shrink: 0;
  }

  .toolbar__title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    white-space: nowrap;
  }

  .toolbar__heading {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    color: var(--sm-contrast);
  }

  .toolbar__count {
    font-size: 0.875rem;
    color: var(--sm-contrast-muted);
    background: var(--sm-gray-100);
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    font-weight: 600;
  }

  .toolbar__search {
    min-width: 280px;
    max-width: 400px;

    :deep(.sm-input__wrapper) {
      background-color: white;
    }
  }

  // Grid layouts
  .group-grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    // Allow cards to have natural height based on content
    grid-auto-rows: auto;
  }

  // Compact add card
  .group-card-add {
    border: 2px dashed var(--sm-border-color);
    border-radius: var(--border-radius);
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 140px;
    // Ensure it matches height of other cards if they grow
    height: 100%;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    background-color: rgba(255, 255, 255, 0.5);

    &:hover {
      border-color: var(--sm-primary);
      background: var(--sm-primary-05);

      .group-card-add__icon {
        background: var(--sm-primary);
        color: white;
        transform: scale(1.1);
      }

      h4 {
        color: var(--sm-primary);
      }
    }
  }

  .group-card-add__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .group-card-add__icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: var(--sm-gray-100);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sm-contrast-muted);
    transition: all 0.2s ease-in-out;
  }

  h4 {
    margin: 0;
    font-weight: 600;
    color: var(--sm-contrast-muted);
    transition: color 0.2s;
  }

  .empty-state {
    grid-row-start: 2;
    grid-row-end: 4;
  }

  .col-span-full {
    grid-column: 1 / -1;
  }

  // Dark mode
  html.dark {
    .toolbar__count {
      background: var(--sm-gray-800);
      color: var(--sm-contrast);
    }

    .toolbar__search :deep(.sm-input__wrapper) {
      background-color: var(--sm-gray-800);
    }

    .group-card-add {
      background-color: rgba(255, 255, 255, 0.02);
      border-color: var(--sm-border-color);

      &:hover {
        border-color: var(--sm-primary);
        background: rgba(var(--sm-primary-rgb), 0.1);
      }
    }

    .group-card-add__icon {
      background: var(--sm-gray-800);
    }
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    .toolbar {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }

    .toolbar__left {
      flex-direction: column;
      align-items: stretch;
      gap: 1rem;
    }

    .toolbar__search {
      max-width: 100%;
      min-width: auto;
    }

    .toolbar__right {
      justify-content: flex-end;
    }

    .group-grid {
      grid-template-columns: 1fr;
    }
  }

  // Global override for drawer body scroll
  // Must be global because SmDrawer structure might prevent deep scoped selection
  :deep(.group-details-drawer .sm-drawer__body) {
    overflow: hidden !important;
    padding-right: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }
</style>
